import { isLocalizable, languageFromPath, storedLanguage } from "./i18n";
import { GITHUB_URL } from "./site";

/**
 * What the site tells Umami beyond what the tracker collects on its own.
 *
 * Lives here rather than in `gatsby-browser.js` for the same reason the wrappers
 * do: the config files stay a thin wiring layer and the logic stays somewhere it
 * can be read on its own.
 */

/**
 * The global the tracker's `data-before-send` attribute names. Exported so
 * `gatsby-ssr.js` writes the attribute from the same value the handler installs
 * itself under - split across two files, a typo in either half would fail
 * silently, costing the language on every event with nothing to show for it.
 *
 * The tracker looks up a flat `window[name]`, so this cannot become a dotted
 * path.
 */
export const BEFORE_SEND_GLOBAL = "hzUmamiBeforeSend";

/**
 * Add Umami's declarative event attributes to repository links generated from
 * news markdown. Keeping this in the template path means future article CTAs
 * inherit the same event contract without authors hand-writing raw HTML.
 *
 * @param {string} html - rendered news article HTML
 * @returns {string} HTML with exact Hezo repository links instrumented
 */
export function instrumentGitHubCtaLinks(html) {
  const href = `href="${GITHUB_URL}"`;
  return html.replaceAll(
    href,
    `${href} data-umami-event="github-cta-blog" data-umami-event-placement="article-body"`,
  );
}

/**
 * Stamp every Umami event with the language the reader is actually seeing.
 *
 * The tracker already sends a `language`, but it reads it off
 * `navigator.language` - what the browser asks for, not what the site rendered.
 * The two part company the moment someone picks a language by hand, so the site
 * locale rides in `tag`, which Umami keeps for exactly this kind of grouping,
 * and the browser's own language stays intact underneath. That one is still
 * worth having: it is what first-visit detection acts on, so the gap between
 * what we auto-picked for a reader and what they went on to choose is only
 * visible while both are recorded.
 *
 * The language is worked out from the path per send rather than captured once at
 * startup. The tracker reads its `data-tag` attribute a single time when it
 * initialises, and the switcher changes language with a pushState navigation, so
 * anything captured at startup would stay pinned to whichever language the
 * reader first landed on for the rest of the session. Reading
 * `window.location.pathname` here instead makes client-side navigation correct
 * without subscribing to route changes.
 *
 * Note this claims `tag` for the language: a `data-tag` added to the script tag
 * later would be overwritten here.
 *
 * @param {string} type - the tracker's event type, unused
 * @param {object} payload - the event about to be sent, mutated in place
 * @returns {object} the payload - a false-y return would cancel the send
 *   outright, so failing to work out the language has to cost a tag and never a
 *   pageview
 */
export function beforeSend(type, payload) {
  try {
    const path = window.location.pathname;
    const served = languageFromPath(path);
    // Docs live only at the unprefixed /docs/*, so their URL always says
    // English while the chrome follows the remembered choice - and the chrome
    // is what the reader is looking at.
    payload.tag = isLocalizable(path) ? served : storedLanguage() || served;
  } catch {
    // Leave the payload as the tracker built it - an untagged event still counts.
  }
  return payload;
}

/**
 * Publish the hook where the tracker can find it.
 *
 * Cannot miss the first pageview: the tracker holds it until
 * `readyState === "complete"`, which is after every deferred script has run,
 * including the bundle this is called from - and it resolves the global per
 * send, not at startup, so everything after is covered regardless.
 */
export function installAnalytics() {
  window[BEFORE_SEND_GLOBAL] = beforeSend;
}
